import { useMemo } from "react";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  CUSTOM_FIELD_TYPE_ICONS,
  CUSTOM_FIELD_TYPE_LABELS,
  type CustomFieldType,
} from "@/lib/customFieldTypes";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

/** A single option parsed from enum/enumNames. */
export interface FieldOption {
  /** The enum value (HubSpot property name) */
  value: string;
  /** Display label (extracted from enumNames, without the group prefix) */
  label: string;
  /** Group name extracted from the `[groupName]` prefix in enumNames */
  groupName: string;
}

/** Sentinel value used for "Not mapped" in the select. */
const NOT_MAPPED_VALUE = "__not_mapped__";

interface CustomFieldRowProps {
  fieldKey: string;
  label: string;
  fieldType: CustomFieldType;
  options: FieldOption[];
  selectedValue: string;
  isMapped: boolean;
  onSelect: (value: string) => void;
  /** When true the row is visually dimmed and the select is non-interactive. */
  disabled?: boolean;
}

/**
 * A single mapping row: FP field label with type icon, mapped state indicator,
 * and grouped HubSpot property dropdown.
 * @param props - CustomFieldRowProps
 * @returns A form row element
 */
export function CustomFieldRow({
  fieldKey,
  label,
  fieldType,
  options,
  selectedValue,
  isMapped,
  onSelect,
  disabled,
}: CustomFieldRowProps) {
  const groups = useMemo(() => groupByName(options), [options]);
  const FieldTypeIcon = CUSTOM_FIELD_TYPE_ICONS[fieldType];

  return (
    <div
      className={cn(
        "flex items-center gap-3 transition-opacity duration-200",
        disabled && "opacity-50",
      )}
    >
      <Label
        htmlFor={`cfm-${fieldKey}`}
        className="flex min-w-0 flex-1 items-center gap-1.5 text-sm"
      >
        <span
          className={cn(
            "inline-block h-1.5 w-1.5 shrink-0 rounded-full transition-colors",
            isMapped ? "bg-emerald-500" : "bg-border",
          )}
        />
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <FieldTypeIcon className="h-3 w-3 shrink-0 text-muted-foreground" />
            </TooltipTrigger>
            <TooltipContent side="top">
              {CUSTOM_FIELD_TYPE_LABELS[fieldType]}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <span className="truncate">{label}</span>
      </Label>
      <Select
        value={selectedValue || NOT_MAPPED_VALUE}
        onValueChange={(value) =>
          onSelect(value === NOT_MAPPED_VALUE ? "" : value)
        }
        disabled={disabled}
      >
        <SelectTrigger id={`cfm-${fieldKey}`} className="w-[300px] shrink-0">
          <SelectValue placeholder="Select property..." />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <SelectItem value={NOT_MAPPED_VALUE}>Not mapped</SelectItem>
          </SelectGroup>
          {groups.map(({ groupLabel, items }) => (
            <SelectGroup key={groupLabel}>
              {groups.length > 1 && <SelectLabel>{groupLabel}</SelectLabel>}
              {items.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectGroup>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Grouping helpers
// ---------------------------------------------------------------------------

interface OptionGroup {
  groupLabel: string;
  items: FieldOption[];
}

/**
 * Groups options by their `groupName`, sorting groups and items alphabetically.
 * @param options - Parsed field options with group names
 * @returns Sorted option groups
 */
function groupByName(options: FieldOption[]): OptionGroup[] {
  const groupMap = new Map<string, FieldOption[]>();

  for (const opt of options) {
    const existing = groupMap.get(opt.groupName);
    if (existing) {
      existing.push(opt);
    } else {
      groupMap.set(opt.groupName, [opt]);
    }
  }

  return Array.from(groupMap.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([groupName, items]) => ({
      groupLabel: formatGroupName(groupName),
      items: items.sort((a, b) => a.label.localeCompare(b.label)),
    }));
}

const KNOWN_SUFFIXES = [
  "information",
  "properties",
  "activity",
  "revenue",
  "details",
];

/**
 * Formats a raw HubSpot property group name into a human-readable title.
 * Handles underscore-separated names (e.g. `deal_activity` -> `Deal Activity`),
 * camelCase names, and concatenated lowercase names by splitting on known
 * suffixes (e.g. `dealinformation` -> `Deal Information`).
 * @param raw - The raw group name from the HubSpot API
 * @returns A formatted, title-cased group label
 */
function formatGroupName(raw: string): string {
  if (!raw) {
    return "Other";
  }

  const words = raw
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .split(" ")
    .flatMap(splitConcatenatedWord);

  return words
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

/**
 * Attempts to split a single concatenated lowercase word (e.g.
 * `dealinformation`) into its component words by matching known suffixes.
 * Returns the word unchanged if no known suffix is found.
 * @param word - A single word segment to split
 * @returns An array of one or two word segments
 */
function splitConcatenatedWord(word: string): string[] {
  const lower = word.toLowerCase();
  for (const suffix of KNOWN_SUFFIXES) {
    if (lower.endsWith(suffix) && lower.length > suffix.length) {
      return [lower.slice(0, -suffix.length), suffix];
    }
  }
  return [word];
}
